import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircleX as CircleXIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext.jsx";
import messagingService, {
  MESSAGES_CHANGED_EVENT,
} from "@/services/messagingService.js";
import supabase from "@/services/supabaseClient.js";
import {
  formatMessagingExplicitTitle,
  resolveMessagingParticipantAvatar,
  resolveMessagingParticipantName,
} from "@/utils/messagingIdentity.js";
import { readLastMessageThreadPath } from "@/utils/messagesNavigationState.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { getPharmacyFolaPrompt } from "@/pages/pharmacy/pharmacyFolaPrompts.js";

const ICON_WRITE = "/icons/appointments/Chat with Fola/write.svg";
const ICON_EMPTY = "/icons/appointments/Chat with Fola/fluent_outline.svg";
const ICON_FOLA = "/assets/images/fola.svg";
const ICON_SEARCH = "/icons/doctor/search.svg";
const ICON_AVATAR_FALLBACK = "/icons/circular-avatar-placeholder.svg";
const DIVIDER_STYLE = { background: "var(--Schemes-Outline-Variant,#E6E7EB)" };
const FOLA_ICON_SIZE = "h-5 w-5";

async function resolveSignedAvatarUrl(path, bucket) {
  const cleanPath = String(path || "").trim();
  if (!cleanPath) return "";
  if (/^https?:\/\//i.test(cleanPath)) return cleanPath;

  try {
    const storage = supabase.storage.from(bucket);
    const { data, error } = await storage.createSignedUrl(cleanPath, 60 * 60 * 24);
    if (!error && data?.signedUrl) return data.signedUrl;
    const { data: publicData } = storage.getPublicUrl(cleanPath);
    return String(publicData?.publicUrl || "").trim();
  } catch {
    return "";
  }
}

function formatInboxTime(value, locale = "en-GB") {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
}

function resolveConversationTitle(conversation, currentUserId, fallbackTitle = "Conversation") {
  const participants = Array.isArray(conversation?.conversation_participants)
    ? conversation.conversation_participants
    : [];
  const other = participants.find((participant) => participant?.user_id !== currentUserId);
  return resolveMessagingParticipantName(other, conversation?.title || fallbackTitle);
}

function resolveConversationPreview(conversation, emptyLabel = "No messages yet.") {
  const content = String(conversation?.lastMessage?.content || "").trim();
  if (content) return content;
  return emptyLabel;
}

function normalizeConversationType(conversation, currentUserId) {
  const title = resolveConversationTitle(conversation, currentUserId);
  if (String(title).toLowerCase() === "fola") return "fola";
  return conversation?.type || "doctor_patient";
}

function isFulfillmentConversation(conversation) {
  const description = String(conversation?.description || "").trim().toLowerCase();
  return description.startsWith("prescription_fulfillment:");
}

function parseFulfillmentPrescriptionId(conversation) {
  const description = String(conversation?.description || "").trim();
  if (!isFulfillmentConversation(conversation)) return "";
  return description.slice("prescription_fulfillment:".length).trim();
}

async function filterValidPatientFulfillmentConversations(conversations = [], currentUserId = "") {
  const cleanUserId = String(currentUserId || "").trim();
  const list = Array.isArray(conversations) ? conversations : [];
  if (!cleanUserId || list.length === 0) return [];

  const fulfillmentConversations = list.filter((conversation) => isFulfillmentConversation(conversation));
  if (fulfillmentConversations.length === 0) return list;

  const prescriptionIds = Array.from(
    new Set(
      fulfillmentConversations
        .map((conversation) => parseFulfillmentPrescriptionId(conversation))
        .filter(Boolean),
    ),
  );
  if (prescriptionIds.length === 0) {
    return list.filter((conversation) => !isFulfillmentConversation(conversation));
  }

  const { data: prescriptions, error: prescriptionError } = await supabase
    .from("prescriptions")
    .select("id,appointment_id,patient_user_id")
    .in("id", prescriptionIds);
  if (prescriptionError) throw prescriptionError;

  const appointmentIds = Array.from(
    new Set(
      (prescriptions || [])
        .map((row) => String(row?.appointment_id || "").trim())
        .filter(Boolean),
    ),
  );
  if (appointmentIds.length === 0) {
    return list.filter((conversation) => !isFulfillmentConversation(conversation));
  }

  const { data: appointments, error: appointmentError } = await supabase
    .from("appointments")
    .select("id,patient_id")
    .in("id", appointmentIds);
  if (appointmentError) throw appointmentError;

  const patientIds = Array.from(
    new Set(
      (appointments || [])
        .map((row) => String(row?.patient_id || "").trim())
        .filter(Boolean),
    ),
  );
  if (patientIds.length === 0) {
    return list.filter((conversation) => !isFulfillmentConversation(conversation));
  }

  const { data: patients, error: patientError } = await supabase
    .from("patients")
    .select("id,user_id")
    .in("id", patientIds);
  if (patientError) throw patientError;

  const patientUserIdByPatientId = new Map(
    (patients || []).map((row) => [String(row?.id || "").trim(), String(row?.user_id || "").trim()]),
  );
  const validPrescriptionIds = new Set(
    (prescriptions || [])
      .filter((row) => {
        const appointment = (appointments || []).find(
          (candidate) => String(candidate?.id || "").trim() === String(row?.appointment_id || "").trim(),
        );
        if (!appointment) return false;
        return (
          patientUserIdByPatientId.get(String(appointment?.patient_id || "").trim()) === cleanUserId &&
          String(row?.patient_user_id || "").trim() === cleanUserId
        );
      })
      .map((row) => String(row?.id || "").trim()),
  );

  return list.filter((conversation) => {
    if (!isFulfillmentConversation(conversation)) return true;
    return validPrescriptionIds.has(parseFulfillmentPrescriptionId(conversation));
  });
}

async function filterAssignedPharmacyFulfillmentConversations(conversations = []) {
  const list = Array.isArray(conversations) ? conversations : [];
  const prescriptionIds = [...new Set(list.filter(isFulfillmentConversation).map(parseFulfillmentPrescriptionId).filter(Boolean))];
  if (prescriptionIds.length === 0) return list;

  // RLS returns only fulfillments assigned to the current admitted owner or
  // active member. The server thread policy independently enforces the same
  // boundary, including direct links and stale participants.
  const { data, error } = await supabase
    .from("prescription_fulfillments")
    .select("prescription_id")
    .in("prescription_id", prescriptionIds);
  if (error) throw error;
  const assignedIds = new Set((data || []).map((row) => String(row?.prescription_id || "")));
  return list.filter((conversation) =>
    !isFulfillmentConversation(conversation) ||
    (assignedIds.has(parseFulfillmentPrescriptionId(conversation)) &&
      Array.isArray(conversation?.messages) && conversation.messages.length > 0),
  );
}

function resolveUnreadCount(conversation) {
  const unreadCount = Number(conversation?.unreadCount || 0);
  if (Number.isFinite(unreadCount) && unreadCount > 0) return unreadCount;
  return conversation?.unread ? 1 : 0;
}

function HeaderIconButton({ label, icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex h-11 w-11 items-center justify-center rounded-[12px]"
    >
      <img
        src={icon}
        alt=""
        className={icon === ICON_FOLA ? FOLA_ICON_SIZE : "h-6 w-6"}
        draggable="false"
      />
    </button>
  );
}

function MessagesHeader({
  onOpenFola,
  onComposeMessage,
  searchValue,
  onSearchChange,
  searchInputRef,
  composeMode = false,
}) {
  const { t } = useLanguage();

  return (
    <div className="shrink-0 bg-[rgb(var(--clr-background))] px-2 pt-4 pb-6">
      <div className="flex min-h-[44px] items-center gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <img
            src={ICON_SEARCH}
            alt=""
            className="h-5 w-5 shrink-0 opacity-80 dark:brightness-0 dark:invert"
            draggable="false"
          />
          <input
            ref={searchInputRef}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={
              composeMode
                ? t("messages.inbox.searchRecipients", "Search recipients")
                : t("messages.inbox.search", "Search")
            }
            inputMode="search"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            autoFocus={composeMode}
            className="flex-1 border-0 bg-transparent text-[16px] leading-6 text-on-surface outline-none placeholder:text-on-surface-var"
          />
          {searchValue ? (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              aria-label={t("messages.inbox.clearSearch", "Clear search")}
              className="grid h-5 w-5 shrink-0 place-items-center text-on-surface-var"
            >
              <CircleXIcon className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        <div className="flex items-center gap-[2px]">
          <HeaderIconButton
            label={t("messages.inbox.openFola", "Open Fola conversation")}
            icon={ICON_FOLA}
            onClick={onOpenFola}
          />
          <HeaderIconButton
            label={t("messages.inbox.newMessage", "New message")}
            icon={ICON_WRITE}
            onClick={onComposeMessage}
          />
        </div>
      </div>

      <div className="pt-4">
        <h1 className="text-title-large text-on-surface">
          {composeMode
            ? t("messages.inbox.composeTitle", "New message")
            : t("messages.inbox.title", "Messages")}
        </h1>
      </div>

      <div
        className="mt-4 h-px w-full"
        style={DIVIDER_STYLE}
      />
    </div>
  );
}

function ScreenCanvas({ children, className = "" }) {
  return (
    <div className={["w-full flex flex-1 flex-col", className].filter(Boolean).join(" ")}>
      {children}
    </div>
  );
}

function ConversationAvatar({ conversation }) {
  const isFolaConversation =
    String(conversation?.type || "").trim().toLowerCase() === "fola" ||
    String(conversation?.title || "").trim().toLowerCase() === "fola" ||
    String(conversation?.avatarUrl || "").trim() === ICON_FOLA;

  if (isFolaConversation) {
    return (
      <img
        src={ICON_FOLA}
        alt=""
        className={`shrink-0 object-contain ${FOLA_ICON_SIZE}`}
        draggable="false"
      />
    );
  }

  if (conversation.avatarUrl) {
    return (
      <img
        src={conversation.avatarUrl}
        alt=""
        className="h-11 w-11 shrink-0 rounded-full object-cover shadow-[0_0_0_1px_rgba(24,50,119,0.10),0_1.5px_0_-0.5px_rgba(26,27,35,0.06),0_3px_3px_-1px_rgba(26,27,35,0.06),0_3px_6px_-2px_rgba(26,27,35,0.06),0_6px_12px_-4px_rgba(29,79,215,0.06),0_12px_24px_-10px_rgba(29,79,215,0.06)]"
        draggable="false"
      />
    );
  }

  return (
    <img
      src={ICON_AVATAR_FALLBACK}
      alt=""
      className="h-11 w-11 shrink-0 rounded-full object-cover"
      draggable="false"
    />
  );
}

export default function MessagesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { languageCode, t } = useLanguage();
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const currentUserId = String(auth?.user?.id || "").trim();
  const currentRole = String(
    auth?.profile?.role || auth?.user?.user_metadata?.role || ""
  ).toLowerCase();
  const currentDisplayName = React.useMemo(() => {
    const profile = auth?.profile || {};
    const userMeta = auth?.user?.user_metadata || {};
    return String(
      profile?.full_name ||
        profile?.display_name ||
        [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
        userMeta?.full_name ||
        userMeta?.name ||
        userMeta?.display_name ||
        ""
    ).trim();
  }, [auth?.profile, auth?.user?.user_metadata]);
  const currentTitledName = React.useMemo(
    () =>
      formatMessagingExplicitTitle(currentDisplayName, {
        isDoctor: currentRole === "doctor",
      }),
    [currentDisplayName, currentRole]
  );
  const [currentAvatarUrl, setCurrentAvatarUrl] = React.useState("");
  const [conversations, setConversations] = React.useState([]);
  const [composeRecipients, setComposeRecipients] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [composeLoading, setComposeLoading] = React.useState(false);
  const [openingRecipientId, setOpeningRecipientId] = React.useState("");
  const [searchValue, setSearchValue] = React.useState("");
  const searchInputRef = React.useRef(null);
  const composeMode = location.state?.compose === true;
  const messagesBasePath = location.pathname.startsWith("/pharmacy/messages")
    ? "/pharmacy/messages"
    : "/messages";
  const pharmacyPromptKey = React.useMemo(
    () =>
      messagesBasePath === "/pharmacy/messages"
        ? String(new URLSearchParams(location.search || "").get("prompt") || "").trim()
        : "",
    [location.search, messagesBasePath],
  );
  const resumePath = React.useMemo(
    () => readLastMessageThreadPath(currentUserId),
    [currentUserId]
  );

  React.useEffect(() => {
    if (location.pathname !== messagesBasePath) return;
    if (messagesBasePath !== "/pharmacy/messages") return;
    if (!pharmacyPromptKey) return;

    const initialDraft = getPharmacyFolaPrompt(pharmacyPromptKey);
    navigate(`${messagesBasePath}/fola?prompt=${encodeURIComponent(pharmacyPromptKey)}`, {
      replace: true,
      state: {
        conversationTitle: "Fola",
        conversationType: "fola",
        returnTo: "/pharmacy/dashboard",
        initialDraft,
        promptKey: pharmacyPromptKey,
      },
    });
  }, [location.pathname, messagesBasePath, navigate, pharmacyPromptKey]);

  React.useEffect(() => {
    if (location.pathname !== messagesBasePath) return;
    if (messagesBasePath !== "/messages") return;
    if (!resumePath) return;
    if (location.state?.forceInbox === true) return;

    navigate(resumePath, { replace: true });
  }, [location.pathname, location.state, messagesBasePath, navigate, resumePath]);

  React.useEffect(() => {
    if (!composeMode) return;
    setSearchValue("");
    window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select?.();
    });
  }, [composeMode]);

  React.useEffect(() => {
    let isMounted = true;

    async function hydrateCurrentAvatar() {
      const profile = auth?.profile || {};
      const userMeta = auth?.user?.user_metadata || {};
      const avatarBucket = currentRole === "doctor" ? "doctor-profile-images" : "patient-profile-images";
      const signedAvatarUrl = await resolveSignedAvatarUrl(
        profile?.profile_photo_path ||
          profile?.profile_photo_storage_path ||
          profile?.photo_path ||
          profile?.avatar_path ||
          "",
        avatarBucket
      );

      const fallbackAvatarUrl =
        signedAvatarUrl ||
        resolveMessagingParticipantAvatar({
          profiles: {
            ...profile,
            avatar_url:
              profile?.avatar_url ||
              profile?.profile_image_url ||
              userMeta?.avatar_url ||
              userMeta?.picture ||
              "",
            profile_image_url:
              profile?.profile_image_url ||
              profile?.avatar_url ||
              userMeta?.avatar_url ||
              userMeta?.picture ||
              "",
            role: currentRole || profile?.role || "",
          },
        }) ||
        "";

      if (isMounted) setCurrentAvatarUrl(fallbackAvatarUrl);
    }

    hydrateCurrentAvatar();
    return () => {
      isMounted = false;
    };
  }, [auth?.profile, auth?.user?.user_metadata, currentRole]);

  const visibleConversations = React.useMemo(() => {
    const query = String(searchValue || "").trim().toLowerCase();
    if (!query) return conversations;
    return conversations.filter((conversation) => {
      const title = String(conversation?.title || "").toLowerCase();
      const preview = String(conversation?.preview || "").toLowerCase();
      return title.includes(query) || preview.includes(query);
    });
  }, [conversations, searchValue]);

  const visibleRecipients = React.useMemo(() => {
    const query = String(searchValue || "").trim().toLowerCase();
    if (!query) return composeRecipients;
    return composeRecipients.filter((recipient) => {
      const title = String(recipient?.title || "").toLowerCase();
      const subtitle = String(recipient?.subtitle || "").toLowerCase();
      return title.includes(query) || subtitle.includes(query);
    });
  }, [composeRecipients, searchValue]);

  React.useEffect(() => {
    let isMounted = true;

    async function loadConversations() {
      setLoading(true);
      try {
        const live = await messagingService.getConversations();
        if (!isMounted) return;
        let scopedLive = Array.isArray(live) ? live : [];
        if (currentRole === "doctor") {
          scopedLive = scopedLive.filter(
            (conversation) => !isFulfillmentConversation(conversation)
          );
        } else if (currentRole === "patient") {
          scopedLive = await filterValidPatientFulfillmentConversations(
            scopedLive,
            currentUserId,
          );
        } else if (currentRole === "pharmacy") {
          scopedLive = await filterAssignedPharmacyFulfillmentConversations(scopedLive);
        }

        if (Array.isArray(scopedLive) && scopedLive.length > 0) {
          setConversations(
            scopedLive.map((conversation) => {
              const participants = Array.isArray(conversation?.conversation_participants)
                ? conversation.conversation_participants
                : [];
              const otherParticipant = participants.find(
                (participant) => participant?.user_id !== currentUserId
              );
              const resolvedTitle = resolveConversationTitle(
                conversation,
                currentUserId,
                t("messages.inbox.fallbackTitle", "Conversation")
              );
              const resolvedAvatarUrl = resolveMessagingParticipantAvatar(otherParticipant);

              return {
                id: conversation.id,
                title: resolvedTitle,
                type: normalizeConversationType(conversation, currentUserId),
                preview: resolveConversationPreview(
                  conversation,
                  t("messages.inbox.noMessagesPreview", "No messages yet.")
                ),
                time: formatInboxTime(
                  conversation?.lastMessage?.created_at || conversation?.updated_at,
                  languageCode
                ),
                unreadCount: resolveUnreadCount(conversation),
                participantUserId: otherParticipant?.user_id || null,
                avatarUrl:
                  resolvedAvatarUrl ||
                  (resolvedTitle &&
                  (resolvedTitle === currentTitledName || resolvedTitle === currentDisplayName)
                    ? currentAvatarUrl
                    : ""),
                source: "live",
              };
            })
          );
          return;
        }

        setConversations([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    void loadConversations();

    const onChanged = () => {
      void loadConversations();
    };
    const onFocus = () => {
      void loadConversations();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void loadConversations();
      }
    };

    const channel = messagingService.subscribeToConversations(currentUserId, onChanged);

    window.addEventListener(MESSAGES_CHANGED_EVENT, onChanged);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      isMounted = false;
      messagingService.unsubscribe(channel);
      window.removeEventListener(MESSAGES_CHANGED_EVENT, onChanged);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [
    currentAvatarUrl,
    currentDisplayName,
    currentTitledName,
    currentUserId,
    languageCode,
    t,
  ]);

  React.useEffect(() => {
    let isMounted = true;

    async function loadComposeRecipients() {
      if (!composeMode) return;

      setComposeLoading(true);
      try {
        const recipients = await messagingService.getComposeRecipients({ currentRole });
        if (!isMounted) return;
        setComposeRecipients(Array.isArray(recipients) ? recipients : []);
      } finally {
        if (isMounted) setComposeLoading(false);
      }
    }

    void loadComposeRecipients();

    const onChanged = () => {
      void loadComposeRecipients();
    };

    if (composeMode) {
      window.addEventListener(MESSAGES_CHANGED_EVENT, onChanged);
    }

    return () => {
      isMounted = false;
      window.removeEventListener(MESSAGES_CHANGED_EVENT, onChanged);
    };
  }, [composeMode, currentRole]);

  async function handleOpenRecipient(recipient) {
    const recipientUserId = String(recipient?.userId || "").trim();
    if (!recipientUserId || openingRecipientId) return;

    if (recipientUserId.toLowerCase() === "fola") {
      navigate(`${messagesBasePath}/fola`, {
        state: {
          conversationTitle: "Fola",
          conversationType: "fola",
        },
      });
      return;
    }

    setOpeningRecipientId(recipientUserId);
    try {
      const conversationType =
        currentRole === "doctor" && String(recipient?.role || "").trim().toLowerCase() === "doctor"
          ? "doctor_doctor"
          : "doctor_patient";
      const conversation = await messagingService.getOrCreateDirectConversation({
        otherUserId: recipientUserId,
        title: recipient?.title || null,
        type: conversationType,
      });

      if (!conversation?.id) return;

      navigate(`${messagesBasePath}/${conversation.id}`, {
        state: {
          conversationTitle:
            recipient?.title || t("messages.inbox.fallbackTitle", "Conversation"),
          conversationType,
          participantUserId: recipientUserId,
        },
      });
    } finally {
      setOpeningRecipientId("");
    }
  }

  async function handleOpenConversation(conversation) {
    const conversationId = String(conversation?.id || "").trim();
    if (!conversationId) return;

    setConversations((current) =>
      (Array.isArray(current) ? current : []).map((item) =>
        item?.id === conversationId ? { ...item, unreadCount: 0 } : item
      )
    );

    if (Number(conversation?.unreadCount || 0) > 0) {
      await messagingService.markMessagesAsRead(conversationId);
    }

    navigate(`${messagesBasePath}/${conversationId}`, {
      state: {
        conversationTitle: conversation.title,
        conversationType: conversation.type,
        participantUserId: conversation.participantUserId || null,
      },
    });
  }

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = documentElement.style.overflow;
    const previousBodyOverscroll = body.style.overscrollBehavior;
    const previousHtmlOverscroll = documentElement.style.overscrollBehavior;

    body.style.overflow = "hidden";
    documentElement.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    documentElement.style.overscrollBehavior = "none";

    return () => {
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overflow = previousHtmlOverflow;
      body.style.overscrollBehavior = previousBodyOverscroll;
      documentElement.style.overscrollBehavior = previousHtmlOverscroll;
    };
  }, []);

  return (
    <div
      data-testid="messages-content-rail"
      className="flex h-full min-h-0 w-full max-w-full flex-col overflow-hidden bg-[rgb(var(--clr-background))]"
    >
      <div className="flex h-full min-h-0 w-full max-w-full flex-col bg-[rgb(var(--clr-background))]">
        <MessagesHeader
          onOpenFola={() =>
            navigate(`${messagesBasePath}/fola`, {
              state: {
                conversationTitle: "Fola",
                conversationType: "fola",
              },
            })
          }
          onComposeMessage={() =>
            navigate(messagesBasePath, {
              replace: location.pathname === messagesBasePath,
              state: {
                forceInbox: true,
                compose: true,
              },
            })
          }
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchInputRef={searchInputRef}
          composeMode={composeMode}
        />

        {composeMode ? (
          composeLoading && composeRecipients.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto px-8 pb-10 pt-10 text-center md:pt-20">
              <img
                src={ICON_EMPTY}
                alt=""
                className="mb-6 h-[76px] w-[76px]"
                draggable="false"
              />
              <h2 className="w-[260px] text-title-large text-on-surface">
                {t(
                  "messages.inbox.loadingRecipientsTitle",
                  "Loading recipients"
                )}
              </h2>
              <p className="mt-3 max-w-[260px] text-body-large text-on-surface-var">
                {t(
                  "messages.inbox.loadingRecipientsBody",
                  "Please wait while we load people you can message."
                )}
              </p>
            </div>
          ) : composeRecipients.length > 0 ? (
            <ScreenCanvas className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain pb-4">
              <div className="pt-[2px]">
                {visibleRecipients.map((recipient, index) => (
                  <button
                    key={recipient.userId}
                    type="button"
                    onClick={() => void handleOpenRecipient(recipient)}
                    disabled={openingRecipientId === recipient.userId}
                    className="relative flex min-h-[88px] w-full items-start gap-3 px-2 py-3 text-left disabled:opacity-70"
                  >
                    <ConversationAvatar conversation={recipient} />

                    <div className="min-w-0 flex-1">
                      <div className="truncate text-body-large text-on-surface">
                        {recipient.title}
                      </div>
                      <p className="mt-px overflow-hidden text-body-medium text-on-surface-var [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                        {recipient.subtitle}
                      </p>
                    </div>

                    <div className="flex min-h-[64px] shrink-0 items-center justify-end pb-[2px]">
                      {openingRecipientId === recipient.userId ? (
                        <span className="text-body-medium text-on-surface-var opacity-60">
                          {t("messages.inbox.opening", "Opening...")}
                        </span>
                      ) : recipient.hasExistingConversation ? (
                        <span className="text-body-medium text-on-surface-var opacity-60">
                          {t("common.open", "Open")}
                        </span>
                      ) : (
                        <span className="text-body-medium text-on-surface-var opacity-60">
                          {t("messages.inbox.new", "New")}
                        </span>
                      )}
                    </div>

                    {index < visibleRecipients.length - 1 ? (
                      <span
                        className="absolute bottom-0 left-0 right-0 h-px"
                        style={DIVIDER_STYLE}
                      />
                    ) : null}
                  </button>
                ))}

                {!composeLoading && visibleRecipients.length === 0 ? (
                  <div className="py-6 text-body-medium text-on-surface-var">
                    {t(
                      "messages.inbox.noRecipientsMatch",
                      "No recipients match your search."
                    )}
                  </div>
                ) : null}
              </div>
            </ScreenCanvas>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto px-8 pb-10 pt-10 text-center md:pt-20">
              <img
                src={ICON_EMPTY}
                alt=""
                className="mb-6 h-[76px] w-[76px]"
                draggable="false"
              />
              <h2 className="w-[260px] text-title-large text-on-surface">
                {t("messages.inbox.noRecipientsTitle", "No recipients yet")}
              </h2>
              <p className="mt-3 max-w-[260px] text-body-large text-on-surface-var">
                {t(
                  "messages.inbox.noRecipientsBody",
                  "Recipients will appear here once you have an appointment or existing conversation."
                )}
              </p>
            </div>
          )
        ) : !loading && conversations.length > 0 ? (
          <ScreenCanvas className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain pb-4">
            <div className="pt-[2px]">
              {visibleConversations.map((conversation, index) => (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => void handleOpenConversation(conversation)}
                  className="relative flex min-h-[88px] w-full items-start gap-3 px-2 py-3 text-left"
                >
                  <ConversationAvatar conversation={conversation} />

                  <div className="min-w-0 flex-1">
                    <div className="truncate text-body-large text-on-surface">
                      {conversation.title}
                    </div>
                    <p className="mt-px overflow-hidden text-body-medium text-on-surface-var [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                      {conversation.preview}
                    </p>
                  </div>

                  <div className="flex min-h-[64px] shrink-0 flex-col items-end justify-between pb-[2px]">
                    <span className="text-body-large text-on-surface-var opacity-60">
                      {conversation.time}
                    </span>
                    {conversation.unreadCount > 0 ? (
                      <span className="inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-[rgb(var(--afrimed-secondary))] px-1 text-[11px] font-medium leading-4 tracking-[-0.264px] text-white">
                        {conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}
                      </span>
                    ) : null}
                  </div>

                  {index < visibleConversations.length - 1 ? (
                    <span
                      className="absolute bottom-0 left-0 right-0 h-px"
                      style={DIVIDER_STYLE}
                    />
                  ) : null}
                </button>
              ))}

              {!loading && visibleConversations.length === 0 ? (
                <div className="py-6 text-body-medium text-on-surface-var">
                  {t(
                    "messages.inbox.noConversationsMatch",
                    "No conversations match your search."
                  )}
                </div>
              ) : null}
            </div>
          </ScreenCanvas>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto px-8 pb-10 pt-10 text-center md:pt-20">
            <img
              src={ICON_EMPTY}
              alt=""
              className="mb-6 h-[76px] w-[76px]"
              draggable="false"
            />
            <h2 className="w-[260px] text-title-large text-on-surface">
              {loading
                ? t("messages.inbox.loadingMessagesTitle", "Loading messages")
                : t("messages.inbox.emptyTitle", "No messages to display")}
            </h2>
            <p className="mt-3 max-w-[260px] text-body-large text-on-surface-var">
              {loading
                ? t(
                    "messages.inbox.loadingMessagesBody",
                    "Please wait while we fetch your latest conversations."
                  )
                : t(
                    "messages.inbox.emptyBody",
                    "Your inbox is currently empty. Check back later!"
                  )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
